// Manual Data - Community Platform
// Esporta tutti i capitoli del manuale utente

export * from './types'

// Capitoli
export { introductionChapter } from './01-introduction'
export { gettingStartedChapter } from './02-getting-started'
export { dashboardChapter } from './03-dashboard'
export { forumChapter } from './04-forum'
export { coursesChapter } from './05-courses'
export { documentsChapter } from './06-documents'
export { eventsChapter } from './07-events'
export { taskforceChapter } from './08-taskforce'
export { clinicChapter } from './09-clinic'
export { gamificationChapter } from './10-gamification'
export { adminChapter } from './11-admin'
export { appendixChapter } from './12-appendix'

// Import per array completo
import { introductionChapter } from './01-introduction'
import { gettingStartedChapter } from './02-getting-started'
import { dashboardChapter } from './03-dashboard'
import { forumChapter } from './04-forum'
import { coursesChapter } from './05-courses'
import { documentsChapter } from './06-documents'
import { eventsChapter } from './07-events'
import { taskforceChapter } from './08-taskforce'
import { clinicChapter } from './09-clinic'
import { gamificationChapter } from './10-gamification'
import { adminChapter } from './11-admin'
import { appendixChapter } from './12-appendix'

import type { ManualChapter } from './types'

/**
 * Array ordinato di tutti i capitoli del manuale
 */
export const manualChapters: ManualChapter[] = [
  introductionChapter,
  gettingStartedChapter,
  dashboardChapter,
  forumChapter,
  coursesChapter,
  documentsChapter,
  eventsChapter,
  taskforceChapter,
  clinicChapter,
  gamificationChapter,
  adminChapter,
  appendixChapter
]

/**
 * Trova un capitolo per ID
 */
export function getChapterById(id: string): ManualChapter | undefined {
  return manualChapters.find(chapter => chapter.id === id)
}

/**
 * Trova un capitolo per numero
 */
export function getChapterByNumber(number: number): ManualChapter | undefined {
  return manualChapters.find(chapter => chapter.number === number)
}

/**
 * Trova una sezione specifica all'interno di un capitolo
 */
export function getSectionById(chapterId: string, sectionId: string) {
  const chapter = getChapterById(chapterId)
  if (!chapter) return undefined
  return chapter.sections.find(section => section.id === sectionId)
}

/**
 * Ottiene tutti gli ID delle sezioni per la navigazione
 */
export function getAllSectionIds(): Array<{ chapterId: string; sectionId: string; title: string }> {
  const sections: Array<{ chapterId: string; sectionId: string; title: string }> = []

  for (const chapter of manualChapters) {
    for (const section of chapter.sections) {
      sections.push({
        chapterId: chapter.id,
        sectionId: section.id,
        title: `${chapter.number}. ${chapter.title} - ${section.title}`
      })
    }
  }

  return sections
}

/**
 * Cerca nel manuale (titoli e contenuti)
 */
export function searchManual(query: string): Array<{
  chapterId: string
  sectionId: string
  chapterTitle: string
  sectionTitle: string
  matchType: 'title' | 'content'
  excerpt?: string
}> {
  const results: Array<{
    chapterId: string
    sectionId: string
    chapterTitle: string
    sectionTitle: string
    matchType: 'title' | 'content'
    excerpt?: string
  }> = []

  const lowerQuery = query.toLowerCase()

  for (const chapter of manualChapters) {
    for (const section of chapter.sections) {
      // Cerca nel titolo
      if (section.title.toLowerCase().includes(lowerQuery)) {
        results.push({
          chapterId: chapter.id,
          sectionId: section.id,
          chapterTitle: chapter.title,
          sectionTitle: section.title,
          matchType: 'title'
        })
      }
      // Cerca nel contenuto
      else if (section.content.toLowerCase().includes(lowerQuery)) {
        // Estrai un excerpt intorno alla query
        const contentLower = section.content.toLowerCase()
        const index = contentLower.indexOf(lowerQuery)
        const start = Math.max(0, index - 50)
        const end = Math.min(section.content.length, index + query.length + 50)
        const excerpt = '...' + section.content.substring(start, end).trim() + '...'

        results.push({
          chapterId: chapter.id,
          sectionId: section.id,
          chapterTitle: chapter.title,
          sectionTitle: section.title,
          matchType: 'content',
          excerpt
        })
      }
    }
  }

  return results
}

/**
 * Numero totale di capitoli
 */
export const TOTAL_CHAPTERS = manualChapters.length

/**
 * Versione del manuale e data aggiornamento (importati dalla versione centralizzata)
 */
export { MANUAL_VERSION, MANUAL_LAST_UPDATE } from '@/version'
